import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "design-system/molecules/Chart"
import { cn } from "design-system/utils"
import { capitalize, formatDate } from "lopermut"
import { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts"
import { GetSentimentResponseDataSchema } from "schema"
import { z } from "zod"

type Props = {
  sentiment: z.infer<typeof GetSentimentResponseDataSchema>["sentiment"]
  className?: string
}

const MIN_WIDTH_TO_SHOW_WEEK_LABEL = 600

export const SentimentChart = ({ sentiment, className }: Props) => {
  const { t } = useTranslation()

  const [showXLabel, setShowXLabel] = useState(false)
  const chartRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleResize = () => {
      setShowXLabel(
        Boolean(
          chartRef.current?.clientWidth &&
          chartRef.current.clientWidth > MIN_WIDTH_TO_SHOW_WEEK_LABEL,
        ),
      )
    }

    handleResize()
    window.addEventListener("resize", handleResize)

    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [])

  const chartConfig = {
    veryPositive: {
      label: t("shared.sentiments.veryPositive"),
    },
    positive: {
      label: t("shared.sentiments.positive"),
    },
    neutral: {
      label: t("shared.sentiments.neutral"),
    },
    negative: {
      label: t("shared.sentiments.negative"),
    },
    veryNegative: {
      label: t("shared.sentiments.veryNegative"),
    },
  } satisfies ChartConfig

  const chartData = useMemo(() => {
    return sentiment.map((item) => ({
      ...item,
      veryPositive: item.VeryPositive ?? 0,
      positive: item.Positive ?? 0,
      neutral: item.Neutral ?? 0,
      negative: item.Negative ?? 0,
      veryNegative: item.VeryNegative ?? 0,
      avgSentiment: item.avgSentiment,
      date: item.date ? capitalize(formatDate(item.date, "MMMM d")) : null,
      axisDate: item.date ? capitalize(formatDate(item.date, "MMM d")) : null,
    }))
  }, [sentiment])

  return (
    <ChartContainer
      config={chartConfig}
      className={cn("aspect-auto w-full h-full", className)}
      ref={chartRef}
    >
      <AreaChart
        accessibilityLayer
        data={chartData}
        margin={{ left: 20, right: 10, top: 20, bottom: 0 }}
      >
        <CartesianGrid vertical={false} />
        <XAxis
          dataKey="date"
          tickLine={false}
          axisLine={false}
          tickMargin={10}
          interval={0}
          tickFormatter={(_, index) => chartData?.[index]?.axisDate ?? ""}
          hide={!showXLabel}
        />
        <YAxis
          tickLine={false}
          axisLine={false}
          tickMargin={10}
          width={30}
          allowDataOverflow={false}
          domain={[0, "auto"]}
        />
        <ChartTooltip cursor={false} content={<ChartTooltipContent />} />
        <Area
          dataKey="veryNegative"
          type="monotone"
          fill="var(--negative)"
          stroke="var(--negative)"
          strokeWidth={1}
          fillOpacity={1}
          stackId="1"
        />
        <Area
          dataKey="negative"
          type="monotone"
          fill="var(--negative-soft)"
          stroke="var(--negative-soft)"
          strokeWidth={1}
          fillOpacity={1}
          stackId="1"
        />
        <Area
          dataKey="neutral"
          type="monotone"
          fill="var(--neutral)"
          stroke="var(--neutral)"
          strokeWidth={1}
          fillOpacity={1}
          stackId="1"
        />
        <Area
          dataKey="positive"
          type="monotone"
          fill="var(--positive-soft)"
          stroke="var(--positive-soft)"
          strokeWidth={1}
          fillOpacity={1}
          stackId="1"
        />
        <Area
          dataKey="veryPositive"
          type="monotone"
          fill="var(--positive)"
          stroke="var(--positive)"
          strokeWidth={1}
          fillOpacity={1}
          stackId="1"
        />
      </AreaChart>
    </ChartContainer>
  )
}
